import { Button } from "../components/Button";
import { noHrefLink, SparkleContext, } from "../context";
import { XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
export const CARD_VARIANTS = [
    "primary",
    "active",
    "secondary",
    "tertiary",
    "highlight",
    "warning",
];
export const CARD_SIZES = ["xs", "sm", "md", "lg"];
const interactiveClasses = cn("s-cursor-pointer", "s-transition s-duration-200", "hover:s-bg-primary-100 dark:hover:s-bg-primary-100-night", "active:s-bg-primary-150 dark:active:s-bg-primary-150-night", "disabled:s-text-primary-muted dark:disabled:s-text-primary-muted-night", "disabled:s-border-border dark:disabled:s-border-border-night", "disabled:s-pointer-events-none", 
// Keyboard focus ring. An `outline` (not a box-shadow ring) is used because the
// Card root is `s-overflow-hidden`, which would clip a ring but leaves an offset
// outline intact. `focus:s-outline-none` drops the always-on default so a mouse
// click stays clean; `focus-visible:` shows the ring on keyboard focus only.
"focus:s-outline-none focus-visible:s-outline-2 focus-visible:s-outline-offset-2 focus-visible:s-outline-highlight-400 dark:focus-visible:s-outline-highlight-400-night");
const cardVariants = cva(cn("s-flex s-text-left s-group", "s-border s-overflow-hidden", "s-text-foreground dark:s-text-foreground-night"), {
    variants: {
        variant: {
            primary: cn("s-bg-muted-background", "s-border-border/0", "dark:s-bg-muted-background-night", "dark:s-border-border-night/0"),
            active: cn("s-bg-muted-background", "s-border-border", "dark:s-bg-muted-background-night", "dark:s-border-border-night"),
            highlight: cn("s-bg-highlight-50", "s-border-border/0", "dark:s-bg-highlight-100-night", "dark:s-border-border-night/0"),
            warning: cn("s-bg-warning-50", "s-border-border/0", "dark:s-bg-warning-50-night", "dark:s-border-border-night/0"),
            secondary: cn("s-bg-background", "s-border-border", "dark:s-bg-background-night", "dark:s-border-border-night"),
            tertiary: cn("s-bg-background", "s-border-border/0", "dark:s-bg-background-night", "dark:s-border-border-night/0"),
        },
        size: {
            xs: "s-px-2 s-py-1.5 s-rounded-lg",
            sm: "s-p-3 s-rounded-xl",
            md: "s-p-4 s-rounded-2xl",
            lg: "s-p-5 s-rounded-3xl",
        },
        selected: {
            true: cn("s-border-highlight-300 dark:s-border-highlight-300-night", "s-ring-2 s-ring-highlight-200/70 dark:s-ring-highlight-300/60", "s-shadow-sm"),
            false: "",
        },
    },
    defaultVariants: {
        variant: "primary",
        size: "md",
        selected: false,
    },
});
const InnerCard = React.forwardRef(({ children, variant, size, className, onClick, href, target = "_blank", rel = "", replace, shallow, selected, isPulsing, style, onKeyDown, tabIndex, ...props }, ref) => {
    const { components } = React.useContext(SparkleContext);
    const Link = href ? components.link : noHrefLink;
    // Determine if the card is interactive based on href or onClick
    const isInteractive = Boolean(href || onClick);
    // A `<div role="button">` has no native keyboard activation, so mirror a
    // button: Enter and Space fire onClick. Space is prevented from scrolling
    // (and Enter from any default) before synthesizing a real click — going
    // through `.click()` keeps onClick's MouseEvent contract intact and composes
    // with any caller-supplied onKeyDown.
    const handleInteractiveKeyDown = (event) => {
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
        if (!event.defaultPrevented &&
            (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            event.currentTarget.click();
        }
    };
    const isSelected = Boolean(selected);
    const hasSelectionProp = typeof selected !== "undefined";
    const cardButtonClassNames = cn(cardVariants({ variant, size, selected: isSelected }), 
    // Apply interactive styles when either href or onClick is present
    isInteractive ? interactiveClasses : "", isPulsing && "s-animate-ring-pulse s-overflow-visible", className);
    const cardStyle = isPulsing ? { animationDuration: "3s", ...style } : style;
    if (href) {
        const linkContent = (React.createElement(Link, { href: href, className: isPulsing ? "s-block s-h-full s-w-full" : cardButtonClassNames, replace: replace, shallow: shallow, target: target, rel: rel, "aria-selected": hasSelectionProp ? isSelected : undefined }, children));
        if (isPulsing) {
            return (React.createElement("div", { className: cardButtonClassNames, style: cardStyle }, linkContent));
        }
        return linkContent;
    }
    return (React.createElement("div", { ref: ref, className: cardButtonClassNames, style: cardStyle, onClick: onClick, role: isInteractive ? "button" : undefined, tabIndex: tabIndex !== null && tabIndex !== void 0 ? tabIndex : (isInteractive ? 0 : undefined), onKeyDown: isInteractive ? handleInteractiveKeyDown : onKeyDown, "aria-pressed": isInteractive && hasSelectionProp ? isSelected : undefined, "aria-selected": hasSelectionProp ? isSelected : undefined, ...props }, children));
});
InnerCard.displayName = "InnerCard";
export const Card = React.forwardRef(({ containerClassName, className, action, ...props }, ref) => {
    return (React.createElement("div", { className: cn("s-group/card s-relative", containerClassName), ref: ref },
        React.createElement(InnerCard, { className: cn("s-h-full s-w-full", className), ...props }),
        action && React.createElement(CardActions, null, action)));
});
Card.displayName = "Card";
const CardActions = React.forwardRef(({ children, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn("s-absolute s-right-2 s-top-2 s-transition-opacity sm:s-opacity-0", "group-focus-within/card:s-opacity-100 group-hover/card:s-opacity-100"), ...props }, children));
});
CardActions.displayName = "CardActions";
export const CardActionButton = React.forwardRef(({ className, variant = "outline", icon = XClose, ...props }, ref) => {
    return (React.createElement(Button, { ref: ref, variant: variant, icon: icon, className: className, ...props }));
});
CardActionButton.displayName = "CardActionButton";
const uncappedGridClasses = cn("@xxs:s-grid-cols-2", "@sm:s-grid-cols-3", "@lg:s-grid-cols-4", "@xl:s-grid-cols-5");
const adaptiveGridClasses = cn("@xxs:has-[>:nth-child(2)]:s-grid-cols-2", "@sm:has-[>:nth-child(3)]:s-grid-cols-3", "@lg:has-[>:nth-child(4)]:s-grid-cols-4", "@xl:has-[>:nth-child(5)]:s-grid-cols-5");
export const CardGrid = React.forwardRef(({ children, className, gridClassName, adaptColumns = false, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn("s-@container", className), ...props },
        React.createElement("div", { className: cn("s-grid s-grid-cols-1 s-gap-2", gridClassName !== null && gridClassName !== void 0 ? gridClassName : (adaptColumns ? adaptiveGridClasses : uncappedGridClasses)) }, children)));
});
CardGrid.displayName = "CardGrid";
//# sourceMappingURL=Card.js.map